import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import AdminDashboard from './pages/AdminDashboard';
import ClientDashboard from './pages/ClientDashboard';
import UsersPage from './pages/UsersPage';
import BudgetsPage from './pages/BudgetsPage';
import CostOverviewPage from './pages/CostOverviewPage';
import ServicesPage from './pages/ServicesPage';
import ComputeCostPage from './pages/ComputeCostPage';
import ComputeCostPageCSV from './pages/CsvBillingPage';
import StorageCostPage from './pages/StorageCostPage';
import InvoicesPage from './pages/InvoicesPage';
import UtilizationPage from './pages/UtilizationPage';
import GoogleUsageReportPage from './pages/GoogleUsageReportPage';

// Protected route wrapper
const Protected = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
};

const LoadingScreen = () => (
  <div style={{
    minHeight: '100vh', background: '#f5f7fa', display: 'flex',
    alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16,
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
  }}>
    <div style={{
      width: 48, height: 48, border: '3px solid rgba(66,133,244,0.2)',
      borderTopColor: '#4285F4', borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    }} />
    <div style={{ color: '#475569', fontSize: 14 }}>Loading...</div>
    <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
  </div>
);

// Placeholder page for unbuilt routes
const PlaceholderPage = ({ title }) => {
  const { isAdmin } = useAuth();
  return (
    <div style={{ display: 'flex', background: '#f5f7fa', minHeight: '100vh', fontFamily: "'DM Sans', sans-serif" }}>
      {/* Inline sidebar import would go here - using dynamic import pattern */}
      <div style={{ marginLeft: 248, flex: 1, padding: '32px 36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🚧</div>
          <h2 style={{ color: '#0f172a', marginBottom: 8 }}>{title}</h2>
          <p style={{ color: '#64748b' }}>This page connects to the GCP Billing API. Add your OAuth credentials to see live data.</p>
        </div>
      </div>
    </div>
  );
};

// Wrapper with Sidebar for placeholder pages
const WithSidebar = ({ title }) => {
  const SidebarWrapper = require('./components/Sidebar').default;
  return (
    <div style={{ display: 'flex', background: '#f5f7fa', minHeight: '100vh', fontFamily: "'DM Sans', sans-serif" }}>
      <SidebarWrapper />
      <div style={{ marginLeft: 248, flex: 1, padding: '32px 36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 60, marginBottom: 20 }}>🚧</div>
          <h2 style={{ color: '#0f172a', marginBottom: 8, fontSize: 24 }}>{title}</h2>
        </div>
      </div>
    </div>
  );
};

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          {/* Client routes */}
          <Route path="/dashboard" element={<Protected><ClientDashboard /></Protected>} />
          <Route path="/users" element={<Protected><UsersPage /></Protected>} />
          {/* <Route path="/services" element={<Protected><ServicesPage /></Protected>} />
          <Route path="/invoices" element={<Protected><InvoicesPage /></Protected>} /> */}
          <Route path="/utilization" element={<Protected><UtilizationPage /></Protected>} />
          <Route path="/google-usage" element={<Protected><GoogleUsageReportPage /></Protected>} />

          {/* Admin routes */}
          <Route path="/admin/dashboard" element={<Protected adminOnly><AdminDashboard /></Protected>} />
          <Route path="/admin/costs" element={<Protected adminOnly><ComputeCostPageCSV /></Protected>} />
          <Route path="/costs" element={<Protected><ComputeCostPageCSV /></Protected>} />
          <Route path="/admin/compute" element={<Protected adminOnly><ComputeCostPage /></Protected>} />
          <Route path="/admin/storage" element={<Protected adminOnly><StorageCostPage /></Protected>} />
          <Route path="/admin/services" element={<Protected adminOnly><ServicesPage /></Protected>} />
          <Route path="/admin/invoices" element={<Protected adminOnly><InvoicesPage /></Protected>} />
          <Route path="/admin/utilization" element={<Protected adminOnly><UtilizationPage /></Protected>} />
          <Route path="/admin/google-usage" element={<Protected adminOnly><GoogleUsageReportPage /></Protected>} />
          <Route path="/admin/budgets" element={<Protected adminOnly><BudgetsPage /></Protected>} />
          <Route path="/admin/users" element={<Protected adminOnly><UsersPage /></Protected>} />
          <Route path="/admin/audit" element={<Protected adminOnly><WithSidebar title="Audit Logs" /></Protected>} />
          <Route path="/admin/settings" element={<Protected adminOnly><WithSidebar title="GCP API Settings" /></Protected>} />

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
